import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
    Send,
    Image,
    X,
    FileText,
    Download,
    Lock,
    AlertTriangle,
    CheckCircle,
    XCircle,
    XOctagon,
    Info,
    Calendar
} from 'lucide-react';
import { chatService } from '../../services/chatService';
import { createSocketConnection } from '../../config/socket.config';
import { inspectionService } from '../../services/inspectionService';
import { format, isSameDay } from 'date-fns';
import { fr, enGB, ar } from 'date-fns/locale';
import { useNotifications } from '../../contexts/NotificationContext';
import { useLanguage } from '../../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';

interface ChatProps {
    inspectionId: string;
    garageId: string;
    garageName: string;
    isDisabled?: boolean;
    onClose?: () => void;
    onMessagesRead?: () => void;
    inspectionStatus?: string;
}

// Helper function to get initials from name
const getInitials = (name: string) => {
    const names = name.split(' ');
    return names
        .map((n) => n[0])
        .join('')
        .toUpperCase();
};

// UserAvatar component avec support RTL
const UserAvatar = ({ name, type }: { name: string; type: string }) => (
    <div
        className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
            type === 'GARAGE'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-600 text-white'
        }`}
    >
        {getInitials(name)}
    </div>
);

// Message système d'un événement de rendez-vous (demande, contre-proposition, confirmation...)
// Affiché dans la langue de l'utilisateur à partir des métadonnées ; `content` (français) sert de repli.
const AppointmentMessage = ({ content, metadata }: { content: string; metadata?: any }) => {
    const { t, isRTL } = useLanguage();
    const navigate = useNavigate();
    const key = metadata?.i18n?.key as string | undefined;
    const params = metadata?.i18n?.params || {};
    const text = key && key.startsWith('appointments.events.') ? String(t(key, { ...params, defaultValue: content })) : content;

    return (
        <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex items-start gap-2 text-indigo-900">
                <Calendar size={18} className="mt-0.5 flex-shrink-0" />
                <p className="text-sm font-medium">{text}</p>
            </div>
            {metadata?.appointmentId && (
                <button
                    onClick={() => navigate(`/dashboard/appointments?id=${metadata.appointmentId}`)}
                    className="mt-2 text-xs font-semibold text-indigo-700 hover:underline"
                >
                    {t('appointments.events.open')}
                </button>
            )}
        </div>
    );
};

// Composant pour améliorer l'affichage des messages de devis
const MessageContent = ({ content }: { content: string }) => {
    const { t, isRTL } = useLanguage();

    // Vérifier si c'est un message de devis
    const isQuoteInfoMessage = content.includes('Un nouveau devis n°') || content.includes('New quote n°') || content.includes('عرض سعر جديد رقم');
    const isQuoteDownloadMessage = content.includes('Télécharger le devis:') || content.includes('Download quote:') || content.includes('تحميل عرض السعر:');

    if (isQuoteInfoMessage) {
        // Extraire les informations du devis
        const quoteNumberMatch = content.match(/n°([A-Z0-9-]+)/) || content.match(/رقم\s*([A-Z0-9-]+)/);
        const quoteNumber = quoteNumberMatch ? quoteNumberMatch[1] : '';

        const vehicleMatch = content.match(/véhicule (.*?)\./) || content.match(/vehicle (.*?)\./) || content.match(/للمركبة (.*?)\./);
        const vehicleInfo = vehicleMatch ? vehicleMatch[1] : '';

        const amountMatch = content.match(/total est de ([0-9.,]+)/) || content.match(/total is ([0-9.,]+)/) || content.match(/المبلغ الإجمالي ([0-9.,]+)/);
        const totalAmount = amountMatch ? amountMatch[1] : '';

        return (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                <div className={`flex items-center gap-2 text-yellow-800 font-medium mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <FileText size={18} />
                    <span>{t('chat.messages.types.quote')} {quoteNumber}</span>
                </div>
                <div className={`text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <p>{t('chat.messages.content.quoteInfo')} <span className="font-medium">{vehicleInfo}</span></p>
                    <p>{t('chat.messages.content.amount')} <span className="font-medium">{totalAmount} {t('chat.currency')} {t('chat.messages.content.ttc')}</span></p>
                </div>
            </div>
        );
    }

    // Si ce n'est pas un message de devis, afficher le contenu normalement
    return <p className={`text-sm ${isRTL ? 'text-right' : 'text-left'}`}>{content}</p>;
};

// Composant pour les messages système spécifiques
const SystemMessageContent = ({ content }: { content: string }) => {
    const { t, isRTL } = useLanguage();

    // Vérifier le type de message système basé sur son contenu
    const isQuoteAccepted = content.includes("a accepté le devis") || content.includes("accepted the quote") || content.includes("قبل عرض السعر");
    const isQuoteRejected = content.includes("a refusé le devis") || content.includes("rejected the quote") || content.includes("رفض عرض السعر");
    const isQuoteCancelled = content.includes("annulé par le garage") || content.includes("cancelled by garage") || content.includes("ألغى الكراج");
    const isConversationStart = content.includes("Début de la conversation") || content.includes("Conversation started") || content.includes("بداية المحادثة");

    if (isQuoteAccepted) {
        return (
            <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                <div className={`flex items-center gap-2 text-green-700 font-medium mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle size={18} />
                    <span>{t('chat.messages.types.quoteAccepted')}</span>
                </div>
                <p className={`text-sm text-green-700 ${isRTL ? 'text-right' : 'text-left'}`}>{content}</p>
            </div>
        );
    } else if (isQuoteRejected) {
        return (
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                <div className={`flex items-center gap-2 text-red-700 font-medium mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <XCircle size={18} />
                    <span>{t('chat.messages.types.quoteRejected')}</span>
                </div>
                <p className={`text-sm text-red-700 ${isRTL ? 'text-right' : 'text-left'}`}>{content}</p>
            </div>
        );
    } else if (isQuoteCancelled) {
        return (
            <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                <div className={`flex items-center gap-2 text-gray-700 font-medium mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <XOctagon size={18} />
                    <span>{t('chat.messages.types.quoteCancelled')}</span>
                </div>
                <p className={`text-sm text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>{content}</p>
            </div>
        );
    } else if (isConversationStart) {
        return (
            <div className="text-center">
                <div className="border-b border-gray-300 w-full mb-2"></div>
                <span className="inline-block bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full">
                    {t('chat.interface.conversationStart')}
                </span>
                <div className="border-b border-gray-300 w-full mt-2"></div>
            </div>
        );
    } else {
        // Message système générique
        return (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <div className={`flex items-center gap-2 text-blue-700 font-medium mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Info size={18} />
                    <span>{t('chat.messages.types.notification')}</span>
                </div>
                <p className={`text-sm text-blue-700 ${isRTL ? 'text-right' : 'text-left'}`}>{content}</p>
            </div>
        );
    }
};

export const ChatInterface: React.FC<ChatProps> = ({
                                                       inspectionId,
                                                       garageId,
                                                       garageName,
                                                       isDisabled = false,
                                                       onClose,
                                                       onMessagesRead,
                                                       inspectionStatus
                                                   }) => {
    const { t, isRTL, currentLanguage } = useLanguage();
    const [messages, setMessages] = useState<any[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [socket, setSocket] = useState<Socket | null>(null);
    const [currentStatus, setCurrentStatus] = useState<string>(inspectionStatus || '');
    const [clientId, setClientId] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [inspectionData, setInspectionData] = useState<any>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Utiliser une référence pour suivre si les messages ont déjà été marqués comme lus
    const messagesMarkedAsRead = useRef(false);

    // Accéder au contexte de notification
    const { setActiveConversation } = useNotifications();

    // Obtenir la locale date-fns appropriée
    const getDateLocale = () => {
        switch(currentLanguage) {
            case 'fr':
                return fr;
            case 'en':
                return enGB;
            default:
                return fr;
        }
    };

    // Générer le conversationId à partir du triplet
    const getConversationId = () => {
        return `${inspectionId}-${garageId}-${clientId}`;
    };

    // Effet pour définir la conversation active lorsque le composant est monté
    useEffect(() => {
        if (clientId) {
            // Informer le contexte de notification que cette conversation est active
            const conversationId = getConversationId();
            setActiveConversation(conversationId);

            // Nettoyer lorsque le composant est démonté
            return () => {
                setActiveConversation(null);
            };
        }
    }, [inspectionId, garageId, clientId, setActiveConversation]);

    // Fonction pour vérifier si le chat est désactivé
    const isChatDisabled = (): boolean => {
        // Si le paramètre isDisabled est explicitement à true
        if (isDisabled) return true;

        // Si le statut est terminé ou rejeté
        if (currentStatus === 'completed' || currentStatus === 'rejected') return true;

        // Si nous avons des données d'inspection, vérifier les conditions supplémentaires
        if (inspectionData) {
            // Si un autre garage a un devis accepté
            if (inspectionData.quotes && inspectionData.quotes.length > 0) {
                const hasAcceptedQuoteFromOtherGarage = inspectionData.quotes.some(
                    (quote: any) => quote.status === 'accepted' && quote.garageId !== garageId
                );
                if (hasAcceptedQuoteFromOtherGarage) return true;
            }

            // Si ce garage a rejeté l'inspection
            if (inspectionData.garageResponses && inspectionData.garageResponses.length > 0) {
                const hasRejectedResponse = inspectionData.garageResponses.some(
                    (response: any) => response.garageId === garageId && response.status === 'rejected'
                );
                if (hasRejectedResponse) return true;
            }
        }

        return false;
    };

    // Mise à jour des données d'inspection
    const refreshInspectionData = async () => {
        try {
            const inspection = await inspectionService.getInspectionById(inspectionId, false);
            setInspectionData(inspection);
            setCurrentStatus(inspection.status);

            if (!clientId && inspection.clientId) {
                setClientId(inspection.clientId);
            }
        } catch (error) {
            console.error('Erreur lors du rafraîchissement des données de l\'inspection:', error);
        }
    };

    // Charger le statut de l'inspection initialement
    useEffect(() => {
        const fetchInspectionStatus = async () => {
            try {
                await refreshInspectionData();
            } catch (error) {
                console.error('Erreur lors du chargement du statut de l\'inspection:', error);
            }
            setIsLoading(false);
        };

        fetchInspectionStatus();
    }, [inspectionId, inspectionStatus]);

    useEffect(() => {
        if (!clientId) return; // Attendre d'avoir le clientId

        const loadMessages = async () => {
            try {
                // Utiliser le nouveau format d'appel avec le triplet
                const data = await chatService.getConversationMessages(inspectionId, clientId);

                // Vérifier si data est un tableau avant de le définir
                if (Array.isArray(data)) {
                    setMessages(data);
                } else if (data && Array.isArray(data.messages)) {
                    // Si la réponse est un objet contenant un tableau messages
                    setMessages(data.messages);
                } else {
                    // Si la structure est différente, initialiser avec un tableau vide
                    console.error('Format de données inattendu:', data);
                    setMessages([]);
                }

                // Marquer les messages comme lus une fois qu'ils sont chargés, si pas déjà fait
                if (!messagesMarkedAsRead.current) {
                    await markMessagesAsRead();
                }
            } catch (error) {
                console.error('Erreur lors du chargement des messages:', error);
                setMessages([]);
            }
        };

        loadMessages();
    }, [inspectionId, clientId]);

    useEffect(() => {
        if (!clientId) return; // Attendre d'avoir le clientId

        const newSocket = createSocketConnection();
        const conversationId = getConversationId();

        // Écouter les nouveaux messages
        newSocket.on(`conversation:${conversationId}`, (message) => {
            setMessages((prev) => [...prev, message]);

            // Si le message vient du client, le marquer comme lu immédiatement
            if (message.sender && message.sender.type === 'CLIENT') {
                markMessagesAsRead();
            }
        });

        // Écouter les événements de messages lus
        newSocket.on(`messages_read:${conversationId}`, () => {
            // Si d'autres utilisateurs ont marqué des messages comme lus
            // Pas nécessaire de faire quelque chose de spécial ici pour cette fonctionnalité
        });

        // Écouter les changements de statut de l'inspection
        newSocket.on(`inspection_status:${inspectionId}`, async (data) => {
            // Mettre à jour le statut
            setCurrentStatus(data.status);

            // Rafraîchir toutes les données de l'inspection
            await refreshInspectionData();
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [inspectionId, clientId, garageId]);

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Fonction pour marquer les messages comme lus
    const markMessagesAsRead = async () => {
        if (!clientId) return;

        try {
            await chatService.markMessagesAsRead(inspectionId, clientId);
            messagesMarkedAsRead.current = true;

            // Notifier le composant parent que les messages ont été lus
            if (onMessagesRead) {
                onMessagesRead();
            }
        } catch (error) {
            console.error('Erreur lors du marquage des messages comme lus:', error);
        }
    };

    const handleSend = () => {
        if (!socket || isChatDisabled() || !clientId) return;

        socket.emit('message', {
            inspectionId: inspectionId,
            garageId: garageId,
            clientId: clientId,
            conversationId: getConversationId(),
            content: newMessage,
            sender: { id: garageId, type: 'GARAGE', name: garageName },
            type: 'TEXT',
        });
        setNewMessage('');
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !socket || isChatDisabled() || !clientId) return;

        // TODO: Implémenter l'upload d'image
        // Pour l'instant, on simule avec une URL de placeholder
        socket.emit('message', {
            inspectionId: inspectionId,
            garageId: garageId,
            clientId: clientId,
            conversationId: getConversationId(),
            content: t('chat.messages.types.imageSent'),
            sender: {
                id: garageId,
                type: 'GARAGE',
                name: garageName
            },
            type: 'IMAGE',
            imageUrl: `https://via.placeholder.com/300x200?text=Image`
        });
    };

    // Déterminer le message d'état pour les chats désactivés
    const getStatusMessage = () => {
        if (inspectionData && inspectionData.quotes && inspectionData.quotes.some(
            (quote: any) => quote.status === 'accepted' && quote.garageId !== garageId
        )) {
            return t('chat.status.otherGarageSelected');
        } else if (currentStatus === 'rejected') {
            return t('chat.status.inspectionRejected');
        } else if (inspectionData && inspectionData.isAvailable === false) {
            return t('chat.status.inspectionUnavailable');
        } else if (inspectionData && inspectionData.garageResponses && inspectionData.garageResponses.some(
            (response: any) => response.garageId === garageId && response.status === 'rejected'
        )) {
            return t('chat.status.youRejected');
        } else if (currentStatus === 'completed') {
            return t('chat.status.inspectionCompleted');
        } else {
            return t('chat.status.disabled');
        }
    };

    // Fonction pour regrouper et rendre les messages par date
    const renderMessages = () => {
        let lastDate: any = null;

        // Filtrer les messages pour ignorer ceux qui contiennent "Conversation démarrée"
        const filteredMessages = messages.filter(message =>
            !(message.sender.type === 'SYSTEM' && (
                message.content.includes("Conversation démarrée") ||
                message.content.includes("Conversation started") ||
                message.content.includes("بدأت المحادثة")
            ))
        );

        return filteredMessages.map((message, index) => {
            const messageDate = new Date(message.timestamp);

            // Déterminer le texte de la date à afficher
            let dateText: string;
            const today = new Date();
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);

            if (isSameDay(messageDate, today)) {
                dateText = t('chat.interface.today');
            } else if (isSameDay(messageDate, yesterday)) {
                dateText = t('chat.interface.yesterday');
            } else {
                dateText = format(messageDate, 'dd MMMM yyyy', { locale: getDateLocale() });
            }

            // Vérifier si c'est un nouveau jour
            const isNewDay = !lastDate || !isSameDay(lastDate, messageDate);
            if (isNewDay) {
                lastDate = messageDate;
            }

            // Vérifier les types de messages
            const isSystemMessage = message.sender.type === 'SYSTEM';
            const isGarageMessage = message.sender.type === 'GARAGE';

            return (
                <React.Fragment key={index}>
                    {isNewDay && (
                        <div className="flex justify-center my-4">
                            <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                                {dateText}
                            </span>
                        </div>
                    )}

                    {(isSystemMessage) ? (
                        // Affichage spécifique pour les messages système et notifications spéciales
                        <div className="flex justify-center my-2">
                            <div className="max-w-[80%]">
                                {(message as any).type === 'APPOINTMENT'
                                    ? <AppointmentMessage content={message.content} metadata={(message as any).metadata} />
                                    : <SystemMessageContent content={message.content} />}
                                <p className="text-xs mt-1 text-center text-gray-500">
                                    {format(new Date(message.timestamp), 'HH:mm', { locale: getDateLocale() })}
                                </p>
                            </div>
                        </div>
                    ) : (
                        // ✅ Affichage RTL pour les autres messages - inversion des côtés
                        <div className={`flex ${
                            isRTL ? (
                                // En RTL: Garage à gauche, Client à droite
                                isGarageMessage ? 'justify-start' : 'justify-end'
                            ) : (
                                // En LTR: Garage à droite, Client à gauche
                                isGarageMessage ? 'justify-end' : 'justify-start'
                            )
                        }`}>
                            <div className={`flex items-end gap-2 max-w-[85%] ${
                                isRTL ? (
                                    isGarageMessage ? '' : 'flex-row-reverse'
                                ) : (
                                    isGarageMessage ? 'flex-row-reverse' : ''
                                )
                            }`}>
                                {/* Afficher l'avatar seulement pour les messages CLIENT */}
                                {!isGarageMessage && (
                                    <UserAvatar
                                        name={message.sender.name}
                                        type={message.sender.type}
                                    />
                                )}

                                <div className={`p-3 rounded-2xl ${
                                    isGarageMessage ? (
                                        // Style pour les messages du garage
                                        isRTL ?
                                            'bg-indigo-600 text-white rounded-bl-none' :
                                            'bg-indigo-600 text-white rounded-br-none'
                                    ) : (
                                        // Style pour les messages du client
                                        isRTL ?
                                            'bg-white text-gray-800 shadow-sm rounded-br-none' :
                                            'bg-white text-gray-800 shadow-sm rounded-bl-none'
                                    )
                                }`}>
                                    <MessageContent content={message.content} />
                                    <p className={`text-xs mt-1 ${
                                        isGarageMessage
                                            ? 'text-indigo-100'
                                            : 'text-gray-500'
                                    } ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {format(new Date(message.timestamp), 'HH:mm', { locale: getDateLocale() })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </React.Fragment>
            );
        });
    };

    // Calculer l'état actuel du chat
    const chatDisabled = isChatDisabled();

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-xl overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className={`p-4 border-b flex justify-between items-center bg-gray-50 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <h3 className={`font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('chat.interface.title')} #{inspectionId.substring(0, 8)}...
                    {chatDisabled && (
                        <span className={`${isRTL ? 'mr-2' : 'ml-2'} inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Lock className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                            {t('chat.interface.locked')}
                        </span>
                    )}
                </h3>
                {onClose && (
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full">
                        <X className="w-5 h-5" />
                    </button>
                )}
            </div>

            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {renderMessages()}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t">
                {chatDisabled ? (
                    <div className={`bg-gray-100 p-3 rounded-lg flex items-center text-gray-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <AlertTriangle className={`w-5 h-5 text-gray-500 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <p className={isRTL ? 'text-right' : 'text-left'}>{getStatusMessage()}</p>
                    </div>
                ) : (
                    <div className={`flex space-x-2 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            placeholder={t('chat.interface.messagePlaceholder')}
                            className={`flex-1 border rounded-full px-4 py-2 focus:outline-none ${
                                chatDisabled
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'focus:ring-2 focus:ring-indigo-500'
                            } ${isRTL ? 'text-right' : 'text-left'}`}
                            disabled={chatDisabled}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <button
                            onClick={handleSend}
                            disabled={!newMessage.trim() || chatDisabled}
                            className="p-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('chat.interface.send')}
                        >
                            <Send className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};